import { capitalCase } from 'change-case';
import formatISO from 'date-fns/formatISO';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { LFC } from '../../../../Legacy';
import { Component, ComponentType } from '../../../../concepts/objects';
import { ExportDimension, ExportDimensionType, ExportDimensionUIProps, ExportValue } from '../ExportDimension';
import { Box, Colors, Input, Select } from './../../../../ui';

const intlFormatter = (dateStyle: any, timeStyle: any) =>
    (value: Date) => {
        return new Intl.DateTimeFormat(navigator.language, { dateStyle, timeStyle } as any).format(value);
    };

const isoFormatter = () =>
    (value: Date) => formatISO(value);

type Formatter = (value: Date) => string;
const FormatterItem = (id: string, label: string, impl: Formatter) => ({ id, label, apply: impl });
type FormatterItem = ReturnType<typeof FormatterItem>;

const formatters = [
    FormatterItem('shortDate', 'Short Date', intlFormatter('short', undefined)),
    FormatterItem('mediumDate', 'Medium Date', intlFormatter('medium', undefined)),
    FormatterItem('longDate', 'Long Date', intlFormatter('full', undefined)),
    FormatterItem('shortDateTime', 'Short Date & Time', intlFormatter('short', 'short')),
    FormatterItem('mediumDateTime', 'Medium Date & Time', intlFormatter('medium', 'medium')),
    FormatterItem('longDateTime', 'Long Date & Time', intlFormatter('full', 'long')),
    FormatterItem('iso', 'ISO 8601', isoFormatter()),
];

const getFormatter = (id: string) => formatters.find(x => x.id === id) ?? null;
const defaultFormatter = getFormatter('shortDateTime');

const ExampleText = styled.div`
    color: ${Colors.gray400};
    font-size: .875rem;
`;

const exampleDate = new Date(1987, 7, 20, 20, 53, 0);

export const DateColumnEditor: LFC<ExportDimensionUIProps> = ({ item, onEdit }) => {

    const selected = getFormatter(item.params.style) ?? defaultFormatter;
    const onChange = useCallback((tm: FormatterItem) => onEdit(ExportDimension.update(item, { style: tm.id })), [item, onEdit]);

    return <Box padding="1" width="28">
        <Input.Frame>
            <Input.Top>
                <Input.Label>Date Format:</Input.Label>
            </Input.Top>
            <Select selected={selected} onChange={onChange} items={formatters} renderItems={
                (items, selectedItem, onSelect) => items.map(tm =>
                    <Select.MenuItem key={tm.id} onClick={onSelect(tm) as any}>
                        <Select.MenuItemText>{tm.label}</Select.MenuItemText>
                        <ExampleText>e.g. {tm.apply(exampleDate)}</ExampleText>
                    </Select.MenuItem>
                )
            } />
        </Input.Frame>
    </Box>;
};

export const DefineDateColumn = <T extends Component>(
    type: ComponentType<T>, label: string, description: string, defaultStyle: string, getter: (obj: T) => any) => {
    const desc = Component.describe(type);
    const id = `${desc.name}:${capitalCase(label)}`;
    return ExportDimensionType({
        id,
        label,
        description,
        ui: DateColumnEditor,
        createInstance(params) {
            return ExportDimension({
                id,
                type: this,
                params: { style: defaultStyle, ...(params ?? {}) },
                label,
                read(rows) {
                    const format = getFormatter(this.params.style) ?? defaultFormatter;
                    const head = [ExportValue(this.id, this.label)];
                    const body = rows.map(c => {
                        const val = c.is(type) ? getter(c as T) : null;
                        return ExportValue(id, (!!val) ? format.apply(val) : '');
                    });
                    return [head.concat(body)];
                }
            });
        }
    });
};
